import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ReactFlow,
  Background,
  Controls,
  type Node,
  type Edge,
  type NodeTypes,
  type NodeProps,
  useNodesState,
  useEdgesState,
  MarkerType,
  Handle,
  Position,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { apiClient } from '@/lib/apiClient';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import {
  ArrowLeft,
  Save,
  Languages,
  Zap,
  Bot,
  Cog,
  Plus,
  X,
  GripVertical,
  Trash2,
  ShieldCheck,
  ArrowUp,
  ArrowDown,
} from 'lucide-react';
import { cn } from '@/lib/cn';
import { SequenceEditorPage } from './SequenceEditorPage';
import { WorkflowLocalizationsModal } from '@/components/workflows/WorkflowLocalizationsModal';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface ChainActionDetail {
  id: string;
  actionType: string;
  actionConfig: Record<string, unknown>;
  actionOrder: number;
  runCondition: unknown | null;
}

interface ChainStepDetail {
  id: string;
  agentKey: string;
  stepOrder: number;
  branch: string;
  joinAfterBranch: string | null;
  inputMapping: Record<string, string>;
  runCondition: unknown | null;
  onFailure: string;
  timeoutMs: number;
  approvalRequired: boolean;
  actions: ChainActionDetail[];
}

interface ChainDetail {
  id: string;
  name: string;
  description: string | null;
  triggerEvent: string;
  triggerConditions: unknown[];
  enabled: boolean;
  steps: ChainStepDetail[];
}

interface AgentDefinitionSummary {
  agentKey: string;
  name: string;
  description: string;
  scope: string;
  category: string;
  runnerType: string;
  defaultConfidenceThreshold: number;
  icon: string | null;
}

/* ------------------------------------------------------------------ */
/*  Layout helpers                                                     */
/* ------------------------------------------------------------------ */

const NODE_WIDTH = 220;
const NODE_HEIGHT = 80;
const H_GAP = 280;
const V_GAP = 160;

function buildGraph(
  chain: ChainDetail,
  agentMap: Map<string, AgentDefinitionSummary>,
): { nodes: Node[]; edges: Edge[] } {
  const nodes: Node[] = [];
  const edges: Edge[] = [];

  // Group steps by branch
  const branchNames = [...new Set(chain.steps.map((s) => s.branch))];
  if (branchNames.length === 0) branchNames.push('main');
  const branchIndex = new Map(branchNames.map((b, i) => [b, i]));

  // Trigger node
  nodes.push({
    id: 'trigger',
    type: 'eventTrigger',
    position: { x: 0, y: branchNames.length > 1 ? ((branchNames.length - 1) * V_GAP) / 2 : 0 },
    data: { triggerEvent: chain.triggerEvent },
    draggable: true,
  });

  // Sort steps by order
  const sorted = [...chain.steps].sort((a, b) => a.stepOrder - b.stepOrder);

  sorted.forEach((step) => {
    const bIdx = branchIndex.get(step.branch) ?? 0;
    const x = (step.stepOrder + 1) * H_GAP;
    const y = bIdx * V_GAP;

    const agentDef = agentMap.get(step.agentKey);

    nodes.push({
      id: step.id,
      type: 'agentStep',
      position: { x, y },
      data: {
        step,
        agentName: agentDef?.name ?? step.agentKey,
        agentDef,
      },
      draggable: true,
    });

    // Action nodes
    step.actions.forEach((action, aIdx) => {
      const actionNodeId = `${step.id}-action-${action.id}`;
      nodes.push({
        id: actionNodeId,
        type: 'actionNode',
        position: { x: x + 20, y: y + NODE_HEIGHT + 20 + aIdx * 50 },
        data: { action },
        draggable: true,
      });
      edges.push({
        id: `e-${step.id}-${actionNodeId}`,
        source: step.id,
        target: actionNodeId,
        type: 'default',
        style: { stroke: '#94a3b8', strokeDasharray: '4 4' },
      });
    });
  });

  // Edges: trigger -> first step(s)
  const firstSteps = new Map<string, ChainStepDetail>();
  sorted.forEach((s) => {
    if (!firstSteps.has(s.branch)) firstSteps.set(s.branch, s);
  });
  firstSteps.forEach((step) => {
    edges.push({
      id: `e-trigger-${step.id}`,
      source: 'trigger',
      target: step.id,
      animated: true,
      markerEnd: { type: MarkerType.ArrowClosed },
      label: step.branch !== 'main' ? step.branch : undefined,
    });
  });

  // Edges: step -> next step in same branch
  const byBranch = new Map<string, ChainStepDetail[]>();
  sorted.forEach((s) => {
    const arr = byBranch.get(s.branch) ?? [];
    arr.push(s);
    byBranch.set(s.branch, arr);
  });
  byBranch.forEach((steps) => {
    for (let i = 0; i < steps.length - 1; i++) {
      const condLabel =
        steps[i + 1].runCondition != null
          ? JSON.stringify(steps[i + 1].runCondition)
          : undefined;
      edges.push({
        id: `e-${steps[i].id}-${steps[i + 1].id}`,
        source: steps[i].id,
        target: steps[i + 1].id,
        animated: true,
        markerEnd: { type: MarkerType.ArrowClosed },
        label: condLabel,
      });
    }
  });

  // Join edges
  sorted.forEach((step) => {
    if (step.joinAfterBranch) {
      const targetBranch = byBranch.get(step.joinAfterBranch);
      if (targetBranch && targetBranch.length > 0) {
        const last = targetBranch[targetBranch.length - 1];
        edges.push({
          id: `e-join-${last.id}-${step.id}`,
          source: last.id,
          target: step.id,
          animated: false,
          style: { stroke: '#94a3b8', strokeDasharray: '6 3' },
          label: 'join',
        });
      }
    }
  });

  return { nodes, edges };
}

/* ------------------------------------------------------------------ */
/*  Custom Nodes                                                       */
/* ------------------------------------------------------------------ */

function EventTriggerNode({ data }: NodeProps) {
  const d = data as { triggerEvent: string };
  return (
    <div
      className="flex items-center gap-2 rounded-xl px-4 py-3 text-white shadow-md"
      style={{
        background: '#1E40AF',
        minWidth: NODE_WIDTH,
        minHeight: 48,
      }}
    >
      <Zap className="h-4 w-4 shrink-0" />
      <div>
        <div className="text-[10px] font-medium uppercase tracking-wide opacity-80">
          Trigger
        </div>
        <div className="text-sm font-semibold">
          {d.triggerEvent.replace(/_/g, ' ')}
        </div>
      </div>
      <Handle type="source" position={Position.Right} className="!bg-card" />
    </div>
  );
}

function AgentStepNode({ data, selected }: NodeProps) {
  const d = data as {
    step: ChainStepDetail;
    agentName: string;
  };
  return (
    <div
      className={cn(
        'rounded-xl border bg-card px-4 py-3 shadow-sm transition-shadow',
        selected && 'ring-2 ring-primary',
      )}
      style={{ minWidth: NODE_WIDTH }}
    >
      <Handle type="target" position={Position.Left} className="!bg-primary" />
      <div className="flex items-center gap-2">
        <Bot className="h-4 w-4 shrink-0 text-primary" />
        <span className="text-sm font-semibold">{d.agentName}</span>
      </div>
      <div className="mt-1 flex flex-wrap gap-1">
        <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-foreground">
          {d.step.branch}
        </span>
        <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-foreground">
          #{d.step.stepOrder}
        </span>
        <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-foreground">
          {d.step.onFailure}
        </span>
        {d.step.approvalRequired && (
          <span className="flex items-center gap-0.5 rounded bg-amber-900/40 px-1.5 py-0.5 text-[10px] font-medium text-amber-300">
            <ShieldCheck className="h-3 w-3" />
            Approval
          </span>
        )}
      </div>
      <Handle type="source" position={Position.Right} className="!bg-primary" />
    </div>
  );
}

function ActionNodeComponent({ data }: NodeProps) {
  const d = data as { action: ChainActionDetail };
  return (
    <div
      className="rounded-lg border bg-card px-3 py-2 text-xs shadow-sm"
      style={{ minWidth: 140 }}
    >
      <Handle type="target" position={Position.Top} className="!bg-zinc-400" />
      <div className="flex items-center gap-1.5">
        <Cog className="h-3 w-3 text-muted-foreground" />
        <span className="font-medium text-foreground">{d.action.actionType}</span>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */

const ON_FAILURE_OPTIONS = ['SKIP', 'ABORT', 'RETRY'] as const;

const ACTION_TYPES = [
  'SEND_EMAIL',
  'CREATE_TASK',
  'UPDATE_FIELD',
  'NOTIFY',
  'WEBHOOK',
  'LOG',
] as const;

/* ------------------------------------------------------------------ */
/*  Main component                                                     */
/* ------------------------------------------------------------------ */

export function ChainEditorPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [editorMode, setEditorMode] = useState<'workflow' | 'sequence'>('workflow');

  const [chain, setChain] = useState<ChainDetail | null>(null);
  const [agentDefs, setAgentDefs] = useState<AgentDefinitionSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [showLocalizations, setShowLocalizations] = useState(false);
  // Story 52.4 — sequence-mode toggle. The graph canvas (ReactFlow) is
  // the right tool for parallel + branching workflows; the sequence
  // shape is right when steps are linear (most outbound campaigns).
  // Default to graph for backward compat — chains created before 52.4
  // had the canvas as the only view.
  const [viewMode, setViewMode] = useState<'graph' | 'sequence'>(() => {
    if (typeof window !== 'undefined') {
      const stored = window.localStorage.getItem('chainEditor.viewMode');
      if (stored === 'sequence' || stored === 'graph') return stored;
    }
    return 'graph';
  });

  const [nodes, setNodes, onNodesChange] = useNodesState<Node>([] as Node[]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([] as Edge[]);

  const nodeTypes: NodeTypes = useMemo(
    () => ({
      eventTrigger: EventTriggerNode,
      agentStep: AgentStepNode,
      actionNode: ActionNodeComponent,
    }),
    [],
  );

  const agentMap = useMemo(
    () => new Map(agentDefs.map((a) => [a.agentKey, a])),
    [agentDefs],
  );

  function flash(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  }

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const [chainRes, agentsRes] = await Promise.all([
        apiClient.get<ChainDetail>(`/agent-chains/${id}`),
        apiClient.get<AgentDefinitionSummary[]>('/agent-definitions'),
      ]);
      setChain(chainRes.data);
      setAgentDefs(agentsRes.data);

      const agMap = new Map(agentsRes.data.map((a) => [a.agentKey, a]));
      const graph = buildGraph(chainRes.data, agMap);
      setNodes(graph.nodes);
      setEdges(graph.edges);
    } catch {
      flash('Failed to load chain');
    } finally {
      setLoading(false);
    }
  }, [id, setNodes, setEdges]);

  useEffect(() => {
    load();
  }, [load]);

  /* ------ Selection handling ------ */

  const selectedStep = useMemo(() => {
    if (!selectedNodeId || !chain) return null;
    return chain.steps.find((s) => s.id === selectedNodeId) ?? null;
  }, [selectedNodeId, chain]);

  const selectedAction = useMemo(() => {
    if (!selectedNodeId || !chain) return null;
    for (const step of chain.steps) {
      for (const action of step.actions) {
        if (selectedNodeId === `${step.id}-action-${action.id}`) {
          return { stepId: step.id, action };
        }
      }
    }
    return null;
  }, [selectedNodeId, chain]);

  /* ------ Step mutations ------ */

  function updateStep(stepId: string, patch: Partial<ChainStepDetail>) {
    if (!chain) return;
    const updated = {
      ...chain,
      steps: chain.steps.map((s) =>
        s.id === stepId ? { ...s, ...patch } : s,
      ),
    };
    setChain(updated);
    // Rebuild graph
    const graph = buildGraph(updated, agentMap);
    setNodes(graph.nodes);
    setEdges(graph.edges);
  }

  function updateAction(
    stepId: string,
    actionId: string,
    patch: Partial<ChainActionDetail>,
  ) {
    if (!chain) return;
    const updated = {
      ...chain,
      steps: chain.steps.map((s) =>
        s.id === stepId
          ? {
              ...s,
              actions: s.actions.map((a) =>
                a.id === actionId ? { ...a, ...patch } : a,
              ),
            }
          : s,
      ),
    };
    setChain(updated);
    const graph = buildGraph(updated, agentMap);
    setNodes(graph.nodes);
    setEdges(graph.edges);
  }

  function addStep(agentKey: string) {
    if (!chain) return;
    const maxOrder = chain.steps.reduce(
      (max, s) => Math.max(max, s.stepOrder),
      -1,
    );
    const newStep: ChainStepDetail = {
      id: `new-${Date.now()}`,
      agentKey,
      stepOrder: maxOrder + 1,
      branch: 'main',
      joinAfterBranch: null,
      inputMapping: {},
      runCondition: null,
      onFailure: 'SKIP',
      timeoutMs: 30000,
      approvalRequired: false,
      actions: [],
    };
    const updated = { ...chain, steps: [...chain.steps, newStep] };
    setChain(updated);
    const graph = buildGraph(updated, agentMap);
    setNodes(graph.nodes);
    setEdges(graph.edges);
    setSelectedNodeId(newStep.id);
  }

  function removeStep(stepId: string) {
    if (!chain) return;
    const updated = {
      ...chain,
      steps: chain.steps
        .filter((s) => s.id !== stepId)
        .map((s, i) => ({ ...s, stepOrder: i })),
    };
    setChain(updated);
    setSelectedNodeId(null);
    const graph = buildGraph(updated, agentMap);
    setNodes(graph.nodes);
    setEdges(graph.edges);
  }

  function addAction(stepId: string) {
    if (!chain) return;
    const step = chain.steps.find((s) => s.id === stepId);
    if (!step) return;
    const newAction: ChainActionDetail = {
      id: `new-action-${Date.now()}`,
      actionType: ACTION_TYPES[0],
      actionConfig: {},
      actionOrder: step.actions.length,
      runCondition: null,
    };
    updateStep(stepId, { actions: [...step.actions, newAction] });
  }

  function removeAction(stepId: string, actionId: string) {
    if (!chain) return;
    const step = chain.steps.find((s) => s.id === stepId);
    if (!step) return;
    updateStep(stepId, {
      actions: step.actions
        .filter((a) => a.id !== actionId)
        .map((a, i) => ({ ...a, actionOrder: i })),
    });
    setSelectedNodeId(null);
  }

  /* ------ Save ------ */

  async function save() {
    if (!chain || !id) return;
    setSaving(true);
    try {
      await apiClient.put(`/agent-chains/${id}`, {
        name: chain.name,
        description: chain.description,
        triggerEvent: chain.triggerEvent,
        triggerConditions: chain.triggerConditions,
        enabled: chain.enabled,
        steps: chain.steps.map((s) => ({
          agentKey: s.agentKey,
          stepOrder: s.stepOrder,
          branch: s.branch,
          joinAfterBranch: s.joinAfterBranch,
          inputMapping: s.inputMapping,
          runCondition: s.runCondition,
          onFailure: s.onFailure,
          timeoutMs: s.timeoutMs,
          approvalRequired: s.approvalRequired,
          actions: s.actions.map((a) => ({
            actionType: a.actionType,
            actionConfig: a.actionConfig,
            actionOrder: a.actionOrder,
            runCondition: a.runCondition,
          })),
        })),
      });
      flash('Chain saved successfully');
    } catch {
      flash('Failed to save chain');
    } finally {
      setSaving(false);
    }
  }

  /* ------ Render ------ */

  if (loading) {
    return (
      <div className="flex h-96 items-center justify-center">
        <p className="text-sm text-muted-foreground">Loading chain editor...</p>
      </div>
    );
  }

  if (!chain) {
    return (
      <div className="flex h-96 items-center justify-center">
        <p className="text-sm text-muted-foreground">Chain not found</p>
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100vh-8rem)] flex-col">
      {/* Toast */}
      {toast && (
        <div className="fixed right-6 top-20 z-50 rounded-lg border bg-card px-4 py-2 text-sm shadow-lg">
          {toast}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between border-b px-1 pb-3">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/admin/chains')}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-lg font-semibold">{chain.name}</h1>
            <p className="text-xs text-muted-foreground">
              {chain.triggerEvent.replace(/_/g, ' ')} &middot;{' '}
              {chain.steps.length} step{chain.steps.length !== 1 ? 's' : ''}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Story 52.4 — view-mode toggle. Sequence mode renders the
              steps as a linear list, the data contract Epic 61 will
              orchestrate against. */}
          <div className="flex rounded-md border bg-muted p-0.5 text-xs">
            <button
              type="button"
              onClick={() => {
                setViewMode('graph');
                if (typeof window !== 'undefined') {
                  window.localStorage.setItem('chainEditor.viewMode', 'graph');
                }
              }}
              className={`rounded px-3 py-1 transition ${
                viewMode === 'graph' ? 'bg-card font-medium shadow-sm' : 'text-muted-foreground'
              }`}
            >
              Graph
            </button>
            <button
              type="button"
              onClick={() => {
                setViewMode('sequence');
                if (typeof window !== 'undefined') {
                  window.localStorage.setItem('chainEditor.viewMode', 'sequence');
                }
              }}
              className={`rounded px-3 py-1 transition ${
                viewMode === 'sequence' ? 'bg-card font-medium shadow-sm' : 'text-muted-foreground'
              }`}
            >
              Sequence
            </button>
          </div>
          {/* Mode toggle — Story 61.2 */}
          <div className="flex rounded-md border overflow-hidden text-xs">
            <button
              className={cn(
                'px-3 py-1.5 font-medium',
                editorMode === 'workflow' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted',
              )}
              onClick={() => setEditorMode('workflow')}
            >
              {t('chainEditor.mode_workflow')}
            </button>
            <button
              className={cn(
                'px-3 py-1.5 font-medium',
                editorMode === 'sequence' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted',
              )}
              onClick={() => setEditorMode('sequence')}
            >
              {t('chainEditor.mode_sequence')}
            </button>
          </div>
          <Button
            variant="outline"
            onClick={() => setShowLocalizations(true)}
            data-testid="chain-localizations"
          >
            <Languages className="mr-1.5 h-4 w-4" />
            {t('wfLocalizations.button')}
          </Button>
          <Button onClick={save} disabled={saving || editorMode === 'sequence'}>
            <Save className="mr-1.5 h-4 w-4" />
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>

      {showLocalizations && chain && (
        <WorkflowLocalizationsModal
          workflowTemplateId={chain.id}
          workflowName={chain.name}
          onClose={() => setShowLocalizations(false)}
        />
      )}

      {/* Sequence mode — swaps out the workflow graph for the sequence step editor */}
      {editorMode === 'sequence' && (
        <div className="flex-1 overflow-y-auto">
          <SequenceEditorPage />
        </div>
      )}

      {/* Body — only shown in workflow mode */}
      {editorMode === 'workflow' && (
      <div className="flex flex-1 overflow-hidden">
        {/* Left sidebar — Agent palette */}
        <div className="w-56 shrink-0 overflow-y-auto border-r bg-card p-3">
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Agent Palette
          </h3>
          <div className="space-y-1.5">
            {agentDefs.map((agent) => (
              <button
                key={agent.agentKey}
                onClick={() => addStep(agent.agentKey)}
                className="flex w-full items-center gap-2 rounded-lg border bg-card px-3 py-2 text-left text-sm transition-colors hover:bg-accent"
              >
                <GripVertical className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-xs font-medium">{agent.name}</div>
                  <div className="truncate text-[10px] text-muted-foreground">
                    {agent.category} &middot; {agent.runnerType}
                  </div>
                </div>
                <Plus className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              </button>
            ))}
            {agentDefs.length === 0 && (
              <p className="text-xs text-muted-foreground">
                No agent definitions available
              </p>
            )}
          </div>
        </div>

        {/* Canvas */}
        <div className="flex-1 overflow-y-auto">
          {viewMode === 'graph' ? (
            <ReactFlow
              nodes={nodes}
              edges={edges}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              nodeTypes={nodeTypes}
              onNodeClick={(_event, node) => setSelectedNodeId(node.id)}
              onPaneClick={() => setSelectedNodeId(null)}
              fitView
              fitViewOptions={{ padding: 0.2 }}
              minZoom={0.3}
              maxZoom={2}
            >
              <Background />
              <Controls />
            </ReactFlow>
          ) : (
            <SequenceList
              chain={chain}
              agentMap={agentMap}
              selectedStepId={selectedNodeId}
              onSelectStep={setSelectedNodeId}
              onMoveStep={(stepId, direction) => {
                if (!chain) return;
                const sorted = [...chain.steps].sort((a, b) => a.stepOrder - b.stepOrder);
                const idx = sorted.findIndex((s) => s.id === stepId);
                if (idx === -1) return;
                const swapWith = direction === 'up' ? idx - 1 : idx + 1;
                if (swapWith < 0 || swapWith >= sorted.length) return;
                // Swap stepOrder values
                const a = sorted[idx];
                const b = sorted[swapWith];
                const updated = chain.steps.map((s) => {
                  if (s.id === a.id) return { ...s, stepOrder: b.stepOrder };
                  if (s.id === b.id) return { ...s, stepOrder: a.stepOrder };
                  return s;
                });
                setChain({ ...chain, steps: updated });
              }}
            />
          )}
        </div>

        {/* Right sidebar — Properties panel */}
        {(selectedStep || selectedAction) && (
          <div className="w-72 shrink-0 overflow-y-auto border-l bg-card p-4">
            {selectedStep && (
              <StepPropertiesPanel
                step={selectedStep}
                agentMap={agentMap}
                onUpdate={(patch) => updateStep(selectedStep.id, patch)}
                onRemove={() => removeStep(selectedStep.id)}
                onAddAction={() => addAction(selectedStep.id)}
                onClose={() => setSelectedNodeId(null)}
              />
            )}
            {selectedAction && (
              <ActionPropertiesPanel
                action={selectedAction.action}
                onUpdate={(patch) =>
                  updateAction(
                    selectedAction.stepId,
                    selectedAction.action.id,
                    patch,
                  )
                }
                onRemove={() =>
                  removeAction(selectedAction.stepId, selectedAction.action.id)
                }
                onClose={() => setSelectedNodeId(null)}
              />
            )}
          </div>
        )}
      </div>
      )} {/* end editorMode === 'workflow' */}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Properties panels                                                  */
/* ------------------------------------------------------------------ */

function StepPropertiesPanel({
  step,
  agentMap,
  onUpdate,
  onRemove,
  onAddAction,
  onClose,
}: {
  step: ChainStepDetail;
  agentMap: Map<string, AgentDefinitionSummary>;
  onUpdate: (patch: Partial<ChainStepDetail>) => void;
  onRemove: () => void;
  onAddAction: () => void;
  onClose: () => void;
}) {
  const agentName = agentMap.get(step.agentKey)?.name ?? step.agentKey;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold">Agent Step</h3>
        <button onClick={onClose}>
          <X className="h-4 w-4 text-muted-foreground" />
        </button>
      </div>

      <div className="rounded-lg border bg-card p-3">
        <div className="flex items-center gap-2">
          <Bot className="h-4 w-4 text-primary" />
          <span className="text-sm font-medium">{agentName}</span>
        </div>
        <p className="mt-1 font-mono text-[10px] text-muted-foreground">
          {step.agentKey}
        </p>
      </div>

      <div>
        <label className="mb-1 block text-xs font-medium">Branch</label>
        <Input
          value={step.branch}
          onChange={(e) => onUpdate({ branch: e.target.value })}
        />
      </div>

      <div>
        <label className="mb-1 block text-xs font-medium">Step Order</label>
        <Input
          type="number"
          value={step.stepOrder}
          onChange={(e) =>
            onUpdate({ stepOrder: parseInt(e.target.value) || 0 })
          }
        />
      </div>

      <div>
        <label className="mb-1 block text-xs font-medium">On Failure</label>
        <select
          value={step.onFailure}
          onChange={(e) => onUpdate({ onFailure: e.target.value })}
          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
        >
          {ON_FAILURE_OPTIONS.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="mb-1 block text-xs font-medium">Timeout (ms)</label>
        <Input
          type="number"
          value={step.timeoutMs}
          onChange={(e) =>
            onUpdate({ timeoutMs: parseInt(e.target.value) || 30000 })
          }
        />
      </div>

      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          id="approval-required"
          checked={step.approvalRequired}
          onChange={(e) =>
            onUpdate({ approvalRequired: e.target.checked })
          }
          className="h-4 w-4 rounded border-input"
        />
        <label htmlFor="approval-required" className="text-xs font-medium">
          Requires Approval
        </label>
      </div>

      <div>
        <label className="mb-1 block text-xs font-medium">
          Input Mapping (JSON)
        </label>
        <textarea
          value={JSON.stringify(step.inputMapping, null, 2)}
          onChange={(e) => {
            try {
              const parsed = JSON.parse(e.target.value) as Record<string, string>;
              onUpdate({ inputMapping: parsed });
            } catch {
              // invalid JSON, ignore
            }
          }}
          rows={3}
          className="flex w-full rounded-md border border-input bg-background px-3 py-2 font-mono text-xs placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
      </div>

      <div>
        <label className="mb-1 block text-xs font-medium">
          Run Condition (JSON, optional)
        </label>
        <textarea
          value={
            step.runCondition != null
              ? JSON.stringify(step.runCondition, null, 2)
              : ''
          }
          onChange={(e) => {
            if (!e.target.value.trim()) {
              onUpdate({ runCondition: null });
              return;
            }
            try {
              onUpdate({ runCondition: JSON.parse(e.target.value) });
            } catch {
              // invalid JSON
            }
          }}
          rows={2}
          className="flex w-full rounded-md border border-input bg-background px-3 py-2 font-mono text-xs placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          placeholder='e.g. {"field":"score","op":">=","value":80}'
        />
      </div>

      <div className="flex gap-2">
        <Button size="sm" variant="outline" onClick={onAddAction}>
          <Plus className="mr-1 h-3 w-3" />
          Action
        </Button>
        <Button size="sm" variant="destructive" onClick={onRemove}>
          <Trash2 className="mr-1 h-3 w-3" />
          Remove
        </Button>
      </div>
    </div>
  );
}

function ActionPropertiesPanel({
  action,
  onUpdate,
  onRemove,
  onClose,
}: {
  action: ChainActionDetail;
  onUpdate: (patch: Partial<ChainActionDetail>) => void;
  onRemove: () => void;
  onClose: () => void;
}) {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold">Action</h3>
        <button onClick={onClose}>
          <X className="h-4 w-4 text-muted-foreground" />
        </button>
      </div>

      <div>
        <label className="mb-1 block text-xs font-medium">Action Type</label>
        <select
          value={action.actionType}
          onChange={(e) => onUpdate({ actionType: e.target.value })}
          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
        >
          {ACTION_TYPES.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="mb-1 block text-xs font-medium">
          Action Config (JSON)
        </label>
        <textarea
          value={JSON.stringify(action.actionConfig, null, 2)}
          onChange={(e) => {
            try {
              const parsed = JSON.parse(e.target.value) as Record<string, unknown>;
              onUpdate({ actionConfig: parsed });
            } catch {
              // invalid JSON
            }
          }}
          rows={6}
          className="flex w-full rounded-md border border-input bg-background px-3 py-2 font-mono text-xs placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          placeholder='{"to":"{{lead.email}}","subject":"Follow-up"}'
        />
      </div>

      <div>
        <label className="mb-1 block text-xs font-medium">
          Run Condition (JSON, optional)
        </label>
        <textarea
          value={
            action.runCondition != null
              ? JSON.stringify(action.runCondition, null, 2)
              : ''
          }
          onChange={(e) => {
            if (!e.target.value.trim()) {
              onUpdate({ runCondition: null });
              return;
            }
            try {
              onUpdate({ runCondition: JSON.parse(e.target.value) });
            } catch {
              // invalid JSON
            }
          }}
          rows={2}
          className="flex w-full rounded-md border border-input bg-background px-3 py-2 font-mono text-xs placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
      </div>

      <Button size="sm" variant="destructive" onClick={onRemove}>
        <Trash2 className="mr-1 h-3 w-3" />
        Remove Action
      </Button>
    </div>
  );
}

/**
 * Story 52.4 — sequence-mode list. Renders chain steps as a vertical
 * ordered list; up/down arrows reorder steps, click selects a step
 * (the right-side properties panel reuses the existing edit UI).
 *
 * The shape here is the data contract Epic 61 (Sequence Engine) will
 * orchestrate against — sequences are linear by definition, so a
 * graph rendering is overkill. Same chain rows under the hood, just
 * a different view.
 */
function SequenceList({
  chain,
  agentMap,
  selectedStepId,
  onSelectStep,
  onMoveStep,
}: {
  chain: ChainDetail | null;
  agentMap: Map<string, AgentDefinitionSummary>;
  selectedStepId: string | null;
  onSelectStep: (id: string | null) => void;
  onMoveStep: (stepId: string, direction: 'up' | 'down') => void;
}) {
  if (!chain) return null;
  const sorted = [...chain.steps].sort((a, b) => a.stepOrder - b.stepOrder);
  if (sorted.length === 0) {
    return (
      <div className="flex h-full items-center justify-center p-8 text-sm text-muted-foreground">
        No steps yet — add one from the Agent Palette on the left.
      </div>
    );
  }
  return (
    <ol className="space-y-2 p-6">
      {sorted.map((step, idx) => {
        const agent = agentMap.get(step.agentKey);
        const isSelected = selectedStepId === step.id;
        return (
          <li
            key={step.id}
            className={cn(
              'flex items-center gap-3 rounded-lg border bg-card p-3 transition',
              isSelected && 'border-primary shadow-sm',
            )}
          >
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
              {idx + 1}
            </span>
            <div
              role="button"
              tabIndex={0}
              onClick={() => onSelectStep(step.id)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onSelectStep(step.id);
                }
              }}
              className="min-w-0 flex-1 cursor-pointer"
            >
              <div className="flex items-center gap-2">
                <span className="truncate text-sm font-medium">
                  {agent?.name ?? step.agentKey}
                </span>
                {step.branch !== 'main' && (
                  <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] text-muted-foreground">
                    {step.branch}
                  </span>
                )}
                {step.approvalRequired && (
                  <span className="rounded-full bg-amber-900/40 px-2 py-0.5 text-[10px] font-medium text-amber-200">
                    Approval
                  </span>
                )}
              </div>
              <div className="text-xs text-muted-foreground">
                {step.actions.length} action{step.actions.length === 1 ? '' : 's'}
                {' · timeout '}
                {Math.round(step.timeoutMs / 1000)}s
                {' · on-failure '}
                {step.onFailure.toLowerCase()}
              </div>
            </div>
            <div className="flex shrink-0 flex-col gap-0.5">
              <Button
                size="sm"
                variant="ghost"
                className="h-6 w-6 p-0"
                onClick={() => onMoveStep(step.id, 'up')}
                disabled={idx === 0}
              >
                <ArrowUp className="h-3.5 w-3.5" />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="h-6 w-6 p-0"
                onClick={() => onMoveStep(step.id, 'down')}
                disabled={idx === sorted.length - 1}
              >
                <ArrowDown className="h-3.5 w-3.5" />
              </Button>
            </div>
          </li>
        );
      })}
    </ol>
  );
}
